import { Component, OnInit } from '@angular/core';
import { environment } from '@lsq-frontend/fasttrack-shared/environment';
import { Color, Modal } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { DashboardExperience } from '../../../constants/experience.constants';
import { ManageUserAction } from '../../../constants/user-settings.constants';
import { ManageUsersService } from '../../services/manage-users.service';

@Component({
    selector: 'permissions-modal',
    template: `
        <div class="modal-content">
            <div class="modal-header">
                <h1>{{ modal?.title }}</h1>
                <lsq-button
                    *ngIf="modal?.showCloseButton"
                    appearance="transparent"
                    class="close"
                    ariaLabel="Close"
                    (buttonClick)="modalService.closeModal()"
                >
                    <lsq-icon aria-hidden="true" [type]="icons.Close" [color]="color.Black">
                    </lsq-icon>
                </lsq-button>
            </div>
            <div *ngIf="isLoading" class="modal-body">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div *ngIf="!isLoading" class="modal-body">
                <ng-container *ngIf="modal.data.action === manageUserAction.Activate" i18n>
                    Once reactivated, this user will be able to access their
                    {{
                        modal.data.clientDashboardExperiences.includes(
                            dashboardExperience.Factoring
                        )
                            ? 'Dashboard'
                            : 'LSQ FastTrack'
                    }}
                    account with the permission you set below.
                </ng-container>
                <ng-container *ngIf="modal.data.action === manageUserAction.Deactivate" i18n>
                    Are you sure you want to deactivate this user's account? Once deactivated, this
                    user will be unable to access their
                    {{
                        modal.data.clientDashboardExperiences.includes(
                            dashboardExperience.Factoring
                        )
                            ? 'Dashboard'
                            : 'LSQ FastTrack'
                    }}
                    account.
                </ng-container>
                <admin-claims
                    *ngIf="isAdminCompany"
                    [readOnly]="modal.data.action === manageUserAction.Deactivate"
                    [allOff]="modal.data.action === manageUserAction.Deactivate"
                    (claimsChange)="saveIsDisabled = false"
                ></admin-claims>

                <permissions
                    *ngIf="!isAdminCompany"
                    [readOnly]="modal.data.action === manageUserAction.Deactivate"
                    [allOff]="modal.data.action === manageUserAction.Deactivate"
                    [modal]="true"
                    (claimsChange)="saveIsDisabled = false"
                >
                </permissions>
            </div>
            <div class="modal-footer ok-btn-showing">
                <div class="modal-footer-left"></div>
                <div class="modal-footer-right">
                    <lsq-button
                        size="sm"
                        [disabled]="
                            isLoading ||
                            (saveIsDisabled && modal.data.action === manageUserAction.Update)
                        "
                        [submitting]="
                            modal && modal.isSubmitting$ ? (modal.isSubmitting$ | async) : false
                        "
                        (buttonClick)="modal?.onOkClick()"
                    >
                        <ng-container *ngIf="modal.data.action === manageUserAction.Activate" i18n>
                            Activate
                        </ng-container>
                        <ng-container
                            *ngIf="modal.data.action === manageUserAction.Deactivate"
                            i18n
                        >
                            Deactivate
                        </ng-container>
                        <ng-container *ngIf="modal.data.action === manageUserAction.Update" i18n>
                            Save
                        </ng-container>
                    </lsq-button>
                </div>
            </div>
        </div>
    `
})
export class PermissionsModalComponent implements OnInit {
    public modal: Modal;
    public isLoading = true;
    public saveIsDisabled = true;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly manageUserAction = ManageUserAction;
    public readonly dashboardExperience = DashboardExperience;

    constructor(
        public modalService: ModalService,
        private _manageUsersService: ManageUsersService
    ) {
        this.modal = this.modalService.modal;
    }

    public async ngOnInit(): Promise<void> {
        const personKey = this.modal.data.personKey;
        const internalCompanyKey = this.modal.data.internalCompanyKey;
        const isFromAdminPage = this.modal.data.isFromAdminPage;
        const clientDashboardExperiences = this.modal.data.clientDashboardExperiences;

        await this._manageUsersService.manageUser(
            personKey,
            internalCompanyKey,
            isFromAdminPage,
            clientDashboardExperiences,
            this.modal.data.isLsqAdminCompany
        );
        this.isLoading = false;
    }

    public get isAdminCompany(): boolean {
        return (
            (this.modal.data.internalCompanyKey === environment.alanRuudCompanyKey ||
                this.modal.data.internalCompanyKey === environment.lsqAdminCompanyKey) &&
            this._manageUsersService.isAdminClaims()
        );
    }
}
